import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

export default function Content({ searchParams }) {
  const [faqs, setFaqs] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [checkAll, setCheckAll] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(0);


  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1); // 카테고리 변경 시 첫 페이지로
    setCheckedItems(new Set()); // 체크박스 초기화
    setCheckAll(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (searchParams?.keyword && currentPage !== 1) {
          setCurrentPage(1);
          return;
        }

        console.log('API 호출:', {
          category: selectedCategory,
          page: currentPage,
          keyword: searchParams?.keyword || '',
          searchType: searchParams?.searchType || '',
        });

        // 파라미터 객체 생성
        const apiParams = {
          curPage: currentPage,
        };

        // 검색 파라미터가 있을 때만 추가
        if (searchParams?.keyword && searchParams.keyword.trim()) {
          apiParams.keyword = searchParams.keyword.trim();
          apiParams.filter = searchParams.searchType || 'title';
        }

        console.log('전송할 파라미터:', apiParams);

        const response = await axios.get(
          `/api/admin/faqVideo/cateId/${selectedCategory}`,
          {
            params: apiParams,
            timeout: 10000,
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        console.log('API 응답:', response.data);

        if (response.data && response.data.success !== false) {
          const faqList = response.data.list || [];
          const paginationData = response.data.pagination || {};

          setFaqs(faqList);
          setPagination(paginationData);

          if (paginationData.totalCount) {
            setTotalCount(paginationData.totalCount);
          }

          // 페이지네이션 계산 로직 수정
          const totalPages = paginationData.totalPages || 1;
          const pageSize = paginationData.pageSize || 10;
          const currentPageForCalc = paginationData.currentPage || currentPage;

          // 페이지 그룹 계산 (10페이지씩 그룹화)
          const pageGroup = Math.floor((currentPageForCalc - 1) / 10);
          const startPage = pageGroup * 10 + 1;
          const endPage = Math.min(startPage + 9, totalPages);

          // 목록 번호 계산 (역순)
          const startNo =
            paginationData.totalCount - (currentPageForCalc - 1) * pageSize;

          const adminPagination = {
            pageCnt: totalPages,
            startPage: startPage,
            endPage: endPage,
            startNo: startNo,
            listCnt: paginationData.totalCount || 0,
            currentPage: currentPageForCalc,
            pageSize: pageSize,
          };

          console.log('계산된 페이지네이션:', adminPagination);
          setPagination(adminPagination);
        } else {
          console.error('API 오류:', response.data.error);
          setError(response.data.error || 'API 호출에 실패');
        }
      } catch (error) {
        console.error('데이터 로드 오류:', error);

        let errorMessage = '데이터를 불러오는 중 오류 발생';

        if (error.response) {
          errorMessage = `서버 오류 (${error.response.status}): ${
            error.response.data.message || error.response.category
          }`;
        } else if (error.request) {
          errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
        } else {
          errorMessage = error.message;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, selectedCategory, searchParams]);

  const handlePageChange = (pageNum) => {
    console.log('페이지 변경:', pageNum);
    setCurrentPage(pageNum);
  };

  const renderPagination = () => {
    if (!pagination || !pagination.pageCnt) {
      return null;
    }

    const pages = [];
    const pageCnt = pagination.pageCnt || 0;
    const startPage = pagination.startPage || 1;
    const endPage = pagination.endPage || 1;

    // 처음으로
    pages.push(
      <li key="first" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.prfaqDefault();
            if (currentPage > 1) handlePageChange(1);
          }}
        >
          《
        </a>
      </li>
    );

    // 이전
    pages.push(
      <li key="prev" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.prfaqDefault();
            if (currentPage > 1) handlePageChange(currentPage - 1);
          }}
        >
          〈
        </a>
      </li>
    );

    // 페이지 번호들
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={i === currentPage ? 'num_on' : 'num'}>
          <a
            href="#"
            onClick={(e) => {
              e.prfaqDefault();
              handlePageChange(i);
            }}
          >
            {i}
          </a>
        </li>
      );
    }

    // 다음
    pages.push(
      <li key="next" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.prfaqDefault();
            if (currentPage < pageCnt) handlePageChange(currentPage + 1);
          }}
        >
          〉
        </a>
      </li>
    );

    // 마지막으로
    pages.push(
      <li key="last" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.prfaqDefault();
            if (currentPage < pageCnt) handlePageChange(pageCnt);
          }}
        >
          》
        </a>
      </li>
    );

    return pages;
  };

  const handleDelete = async () => {
    if (checkedItems.size === 0) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }

    if (window.confirm('선택한 항목을 삭제하시겠습니까?')) {
      try {
        const deleteIds = Array.from(checkedItems);

        const response = await axios.post('/api/admin/faqVideo/delete', {
          ids: deleteIds,
        });

        if (response.data.success) {
          alert(response.data.message || '선택한 항목이 삭제되었습니다.');
          setCheckedItems(new Set());
          setCheckAll(false);
          // 현재 페이지 데이터 새로고침
          window.location.reload();
        } else {
          alert(response.data.message || '삭제 처리 중 오류가 발생했습니다.');
        }
      } catch (error) {
        console.error('삭제 오류:', error);
        alert(
          '삭제 중 오류가 발생했습니다: ' +
            (error.response?.data?.message || error.message)
        );
      }
    }
  };

  const handleCheckAll = (e) => {
    const isChecked = e.target.checked;
    setCheckAll(isChecked);

    if (isChecked) {
      const allIds = new Set(faqs.map((faq) => faq.fv_no || faq.id));
      setCheckedItems(allIds);
    } else {
      setCheckedItems(new Set());
    }
  };

  const handleCheckItem = (faqId) => {
    const newCheckedItems = new Set(checkedItems);

    if (newCheckedItems.has(faqId)) {
      newCheckedItems.delete(faqId);
    } else {
      newCheckedItems.add(faqId);
    }

    setCheckedItems(newCheckedItems);
    setCheckAll(newCheckedItems.size === faqs.length && faqs.length > 0);
  };

  if (loading) {
    return (
      <div className="txt-center" style={{ marginTop: '60px' }}>
        로딩 중...
      </div>
    );
  }

  return (
    <>
      {/* 카테고리 필터 라디오 버튼 */}
      <div className="row-flex-center gap-20">
        <div className="row-flex-center gap-10 mt-2">
          <input
            type="radio"
            value={0}
            id="category1"
            name="faqCategory"
            checked={selectedCategory === 0}
            onChange={() => handleCategoryChange(0)}
          />
          <label htmlFor="category1" className="prt-regular fs-16 pointer">
            자주묻는질문
          </label>
        </div>

        <div className="row-flex-center gap-10 mt-2">
          <input
            type="radio"
            value={1}
            id="category2"
            name="faqCategory"
            checked={selectedCategory === 1}
            onChange={() => handleCategoryChange(1)}
          />
          <label htmlFor="category2" className="prt-regular fs-16 pointer">
            자가관리방법
          </label>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div
          className="error-message"
          style={{ color: 'red', margin: '20px 0' }}
        >
          {error}
        </div>
      )}

      {/* 데이터 테이블 */}
      <div className="over_hi" style={{ marginTop: '60px' }}>
        <form id="deleteFrm" method="post">
          <table className="adminTable">
            <colgroup>
              <col style={{ width: '5%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '50%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    id="checkall"
                    checked={checkAll}
                    onChange={handleCheckAll}
                  />
                </th>
                <th>No</th>
                <th>카테고리</th>
                <th>제목</th>
                <th>작성자</th>
                <th>작성날짜</th>
              </tr>
            </thead>
            <tbody>
              {faqs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="txt-center">
                    {selectedCategory === 0 ? '자주묻는질문' : '자가관리방법'}{' '}
                    없습니다.
                  </td>
                </tr>
              ) : (
                faqs.map((faq, index) => {
                  const faqId = faq.fv_no || faq.id || faq.ID;
                  const faqTitle = faq.fv_title || faq.title || faq.TITLE;
                  const faqCategory =
                    faq.fv_category1 !== undefined
                      ? faq.fv_category1
                      : faq.category;
                  const faqDate = faq.fv_add_dt || faq.regDate;

                  // 디버깅용 로그 - 전체 faq 객체와 카테고리 값 확인
                  console.log('Full Faq Object:', faq);
                  console.log('Faq Category Details:', {
                    fv_category1: faq.fv_category1,
                    category: faq.category,
                    finalCategory: faqCategory,
                    categoryType: typeof faqCategory,
                    comparison: faqCategory == 0,
                  });

                  return (
                    <tr key={faqId}>
                      <td>
                        <input
                          type="checkbox"
                          value={faqId}
                          name="check"
                          checked={checkedItems.has(faqId)}
                          onChange={() => handleCheckItem(faqId)}
                        />
                      </td>
                      <td>
                        {pagination.startNo && pagination.startNo > 0
                          ? pagination.startNo - index
                          : (totalCount || 0) - (currentPage - 1) * 10 - index}
                      </td>
                      <td className="txt-center">
                        <span
                          className={`status-badge ${
                            faqCategory === 0 ? 'status-active' : 'status-ended'
                          }`}
                        >
                          {faq.statusText ||
                            (faqCategory === 0
                              ? '자주묻는질문'
                              : '자가관리방법')}
                        </span>
                      </td>
                      <td className="left">
                        <a href={`/admin/faq_d/${faqId}`}>{faqTitle}</a>
                      </td>
                      <td className="txt-center">관리자</td>
                      <td className="txt-center">
                   {format(new Date(faqDate), 'yyyy/MM/dd HH:mm')}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </form>
      </div>

      {/* 페이지네이션 */}
      <ul className="txt-center pageing">{renderPagination()}</ul>

      {/* 액션 버튼들 */}
      <div className="row-flex flex-end gap-10">
        <input
          type="button"
          value="삭제"
          className="common-del"
          onClick={handleDelete}
        />
        <input
          type="button"
          value="글쓰기"
          className="common-del"
          onClick={() => (window.location.href = '/admin/faq_w')}
        />
      </div>
    </>
  );
}
